import type { RecurrenceValue, RecurrenceSchedule, WeekDay } from '@/types/recurrence';

type ScheduleArrayKey = 'hours' | 'minutes' | 'monthDays';

/**
 * Return a new recurrence with the given schedule, or with the schedule
 * removed entirely when empty.
 */
function withSchedule(recurrence: RecurrenceValue, schedule: RecurrenceSchedule): RecurrenceValue {
  const updated = { ...recurrence };
  if (Object.keys(schedule).length > 0) {
    updated.schedule = schedule;
  } else {
    delete updated.schedule;
  }
  return updated;
}

/**
 * Update the recurrence schedule by setting/clearing an array field,
 * cleaning up empty schedules automatically.
 */
function updateScheduleArray(
  recurrence: RecurrenceValue,
  key: ScheduleArrayKey,
  values: number[] | undefined
): RecurrenceValue {
  const schedule: RecurrenceSchedule = recurrence.schedule ? { ...recurrence.schedule } : {};

  if (values && values.length > 0) {
    schedule[key] = values;
  } else {
    delete schedule[key];
  }

  return withSchedule(recurrence, schedule);
}

/**
 * Add a number to a schedule array (dedupe + sort).
 */
export function addScheduleItem(
  recurrence: RecurrenceValue,
  key: ScheduleArrayKey,
  value: number
): RecurrenceValue {
  const current = recurrence.schedule?.[key] ? [...recurrence.schedule[key]] : [];
  if (!current.includes(value)) {
    current.push(value);
    current.sort((a, b) => a - b);
  }
  return updateScheduleArray(recurrence, key, current);
}

/**
 * Remove a number from a schedule array.
 */
export function removeScheduleItem(
  recurrence: RecurrenceValue,
  key: ScheduleArrayKey,
  value: number
): RecurrenceValue {
  const current = recurrence.schedule?.[key]?.filter((v) => v !== value);
  return updateScheduleArray(recurrence, key, current);
}

/**
 * Toggle a weekday in schedule.weekDays.
 */
export function toggleWeekDay(
  recurrence: RecurrenceValue,
  day: WeekDay,
  checked: boolean
): RecurrenceValue {
  const schedule: RecurrenceSchedule = recurrence.schedule ? { ...recurrence.schedule } : {};
  const weekDays = schedule.weekDays ? [...schedule.weekDays] : [];

  if (checked && !weekDays.includes(day)) {
    weekDays.push(day);
  } else if (!checked) {
    const index = weekDays.indexOf(day);
    if (index >= 0) weekDays.splice(index, 1);
  }

  if (weekDays.length > 0) {
    schedule.weekDays = weekDays;
  } else {
    delete schedule.weekDays;
  }

  return withSchedule(recurrence, schedule);
}
